import { reactive, computed, ref } from 'vue';
import { defineStore } from 'pinia';
export const useSignupStore = defineStore('signup', () => {
    const signUpType = ref(undefined);
    const corporateDocumentGuideAcknowledged = ref(false);
    const terms = reactive({
        TERMS_OF_USE: false,
        OPEN_API_TERMS_OF_USE: false,
        PERSONAL_INFO_COLLECTION: false,
        RISK_DISCLOSURE: false,
        FAIS_DISCLOSURE: false,
        POPIA_POLICY: false,
        MARKETING_NOTIFICATION: false,
    });
    const userInfo = reactive({
        ci: '',
        name: '', //이름(한글)
        email: '', //이메일
        phoneNo: '', //휴대폰번호
        birth: '', //생년월일
        password: '', //비밀번호
    });
    const individualInfo = reactive({
        lastNameEn: '', //성(영문)
        firstNameEn: '', //이름(영문)
        address: '', //주소(한글)
        detailAddress: '', //상세주소(한글)
        addressEn: '', //주소(영문)
        detailAddressEn: '', //상세주소(영문)
        zipCode: '', //우편번호
        idCard: null, //신분증(주민등록증 또는 운전면허증)
        additionalIdDocument: null, //신분증 초본(영문)
    });
    const corporateInfo = reactive({
        companyName: '', //법인명
        businessRegistrationNumber: '', //사업자등록번호
        businessNumber: '', //법인등록번호
        representativeLastName: '', //법인대표 성(영문)
        representativeFirstName: '', //법인대표 이름(영문)
        representativeKorName: '', //법인대표 이름(한글)
        representativeBirth: '', //법인대표 생년월일
        address: '', //법인주소
        detailAddress: '', //법인주소 상세
        addressEn: '', //법인주소(영문)
        detailAddressEn: '', //법인주소 상세(영문)
        zipCode: '', //우편번호
        businessRegistration: null, //사업자등록증명원
        corporateRepresentative: null, //법인대표 초본(영문)
        billPaymentCorporate: null, //법인명 공과금 납부서
        shareholderRegister: null, //주주 명부(영문 공증 필요)
        corporateRepresentativePassport: null, //법인대표 여권 사본
        additionalCorporateRepresentativePassport: [], //법인대표 여권 사본(2명 이상일 경우)
    });
    /**
     * 개인 회원가입에 필요한 정보를 반환합니다.
     * 백엔드 API 스펙에 맞게 individualMember와 파일들을 분리하여 반환합니다.
     */
    const getIndividualSignupInfo = computed(() => {
        return {
            individualMember: {
                ci: userInfo.ci,
                email: userInfo.email,
                password: userInfo.password,
                name: userInfo.name,
                phoneNo: userInfo.phoneNo,
                birth: userInfo.birth,
                firstNameEn: individualInfo.firstNameEn,
                lastNameEn: individualInfo.lastNameEn,
                address: individualInfo.address,
                addressEn: individualInfo.addressEn,
                detailAddress: individualInfo.detailAddress,
                detailAddressEn: individualInfo.detailAddressEn,
                zipCode: individualInfo.zipCode,
                advertisingAgreed: terms.MARKETING_NOTIFICATION,
            },
            idCard: individualInfo.idCard ?? undefined,
            additionalIdDocument: individualInfo.additionalIdDocument ?? undefined,
        };
    });
    /**
     * 법인 회원가입에 필요한 정보를 반환합니다.
     * 백엔드 API 스펙에 맞게 corporateMember와 파일들을 분리하여 반환합니다.
     */
    const getCorporateSignupInfo = computed(() => {
        return {
            corporateMember: {
                ci: userInfo.ci,
                email: userInfo.email,
                password: userInfo.password,
                name: userInfo.name,
                phoneNo: userInfo.phoneNo,
                birth: userInfo.birth,
                companyName: corporateInfo.companyName,
                businessRegistrationNumber: corporateInfo.businessRegistrationNumber,
                businessNumber: corporateInfo.businessNumber,
                address: corporateInfo.address,
                addressEn: corporateInfo.addressEn,
                detailAddress: corporateInfo.detailAddress,
                detailAddressEn: corporateInfo.detailAddressEn,
                zipCode: corporateInfo.zipCode,
                representativeEngFirstName: corporateInfo.representativeFirstName,
                representativeEngLastName: corporateInfo.representativeLastName,
                representativeKorName: corporateInfo.representativeKorName,
                representativeBirth: corporateInfo.representativeBirth,
                advertisingAgreed: terms.MARKETING_NOTIFICATION,
            },
            businessRegistration: corporateInfo.businessRegistration ?? undefined,
            corporateRepresentative: corporateInfo.corporateRepresentative ?? undefined,
            billPaymentCorporate: corporateInfo.billPaymentCorporate ?? undefined,
            shareholderRegister: corporateInfo.shareholderRegister ?? undefined,
            corporateRepresentativePassport: corporateInfo.corporateRepresentativePassport ?? undefined,
            additionalCorporateRepresentativePassport: corporateInfo.additionalCorporateRepresentativePassport &&
                corporateInfo.additionalCorporateRepresentativePassport.length > 0
                ? corporateInfo.additionalCorporateRepresentativePassport
                : undefined,
        };
    });
    /**
     * 약관 동의 정보를 업데이트합니다.
     * @param value - 약관 동의 정보
     */
    const updateTerms = (value) => {
        Object.assign(terms, value);
    };
    /**
     *
     * @param value 사용자 정보
     *
     * @description 휴대폰인증(PASS) 후 얻은 사용자 정보를 업데이트합니다.
     */
    const updateUserInfo = (value) => {
        userInfo.ci = value.ci;
        userInfo.name = value.name;
        userInfo.phoneNo = value.phoneNo;
        userInfo.birth = value.birth;
    };
    /**
     *
     * @param value 이메일
     *
     * @description 이메일 검증 후 이메일 정보를 업데이트합니다.
     */
    const updateUserEmail = (value) => {
        userInfo.email = value;
    };
    /**
     *
     * @param value 비밀번호
     *
     * @description 비밀번호 유효성검사 후 비밀번호 정보를 업데이트합니다.
     */
    const updateUserPassword = (value) => {
        userInfo.password = value;
    };
    /**
     *
     * @description 휴대폰인증(PASS)으로 얻은 사용자 정보를 초기화합니다.
     */
    const resetUserInfo = () => {
        userInfo.ci = '';
        userInfo.name = '';
        userInfo.phoneNo = '';
        userInfo.birth = '';
    };
    /**
     *
     * @description 이메일 정보를 초기화합니다.
     */
    const resetUserEmail = () => {
        userInfo.email = '';
    };
    /**
     *
     * @description 비밀번호 정보를 초기화합니다.
     */
    const resetUserPassword = () => {
        userInfo.password = '';
    };
    /**
     *
     * @description 약관 동의 정보를 초기화합니다.
     */
    const resetTerms = () => {
        terms.TERMS_OF_USE = false;
        terms.PERSONAL_INFO_COLLECTION = false;
        terms.RISK_DISCLOSURE = false;
        terms.FAIS_DISCLOSURE = false;
        terms.POPIA_POLICY = false;
        terms.MARKETING_NOTIFICATION = false;
    };
    /**
     *
     * @param value 개인 회원가입 정보
     *
     * @description 개인 회원가입 시 필요한 정보를 업데이트합니다.
     */
    const updateIndividual = (value) => {
        individualInfo.lastNameEn = value.lastNameEn;
        individualInfo.firstNameEn = value.firstNameEn;
        individualInfo.address = value.address;
        individualInfo.detailAddress = value.detailAddress;
        individualInfo.addressEn = value.addressEn;
        individualInfo.detailAddressEn = value.detailAddressEn;
        individualInfo.zipCode = value.zipCode;
    };
    /**
     *
     * @param value 개인 회원가입 정보
     *
     * @description 개인 회원가입 시 필요한 파일을 업데이트합니다.
     */
    const uploadIndividualDocument = (individualDocument, additionalIdDocument) => {
        individualInfo.idCard = individualDocument;
        individualInfo.additionalIdDocument = additionalIdDocument;
    };
    /**
     * 개인 회원가입 시 업로드한 파일 정보를 초기화합니다.
     */
    const resetIndividualDocument = () => {
        individualInfo.idCard = null;
        individualInfo.additionalIdDocument = null;
    };
    /**
     *
     * @param value 법인 회원가입 정보
     *
     * @description 법인 회원가입 시 필요한 정보를 업데이트합니다.
     */
    const updateCorporate = (value) => {
        corporateInfo.companyName = value.companyName;
        corporateInfo.businessRegistrationNumber = value.businessRegistrationNumber;
        corporateInfo.businessNumber = value.businessNumber;
        corporateInfo.representativeLastName = value.representativeLastName;
        corporateInfo.representativeFirstName = value.representativeFirstName;
        corporateInfo.representativeKorName = value.representativeKorName;
        corporateInfo.representativeBirth = value.representativeBirth;
        corporateInfo.address = value.address;
        corporateInfo.detailAddress = value.detailAddress;
        corporateInfo.addressEn = value.addressEn;
        corporateInfo.detailAddressEn = value.detailAddressEn;
        corporateInfo.zipCode = value.zipCode;
    };
    /**
     *
     * @param value 법인 회원가입 정보
     *
     * @description 법인 회원가입 시 필요한 파일을 업데이트합니다.
     */
    const uploadCorpInfoDocument = (businessRegistration, corporateRepresentative, billPaymentCorporate) => {
        corporateInfo.businessRegistration = businessRegistration;
        corporateInfo.corporateRepresentative = corporateRepresentative;
        corporateInfo.billPaymentCorporate = billPaymentCorporate;
    };
    /**
     *
     * @param value 법인 대표 여권 사본 (1개 이상)
     *
     * @description 법인 회원가입 시 필요한 법인대표 관련 파일을 업데이트합니다.
     */
    const uploadCorpAdminDocument = (shareholderRegister, corporateRepresentativePassport, additionalCorporateRepresentativePassport) => {
        corporateInfo.shareholderRegister = shareholderRegister;
        corporateInfo.corporateRepresentativePassport = corporateRepresentativePassport;
        corporateInfo.additionalCorporateRepresentativePassport =
            additionalCorporateRepresentativePassport;
    };
    /**
     * 법인 회원가입 시 업로드한 파일 정보를 초기화합니다.
     */
    const resetCorporateDocument = () => {
        corporateInfo.businessRegistration = null;
        corporateInfo.corporateRepresentative = null;
        corporateInfo.billPaymentCorporate = null;
        corporateInfo.shareholderRegister = null;
        corporateInfo.corporateRepresentativePassport = null;
        corporateInfo.additionalCorporateRepresentativePassport = [];
    };
    /**
     * 회원가입 스토어의 모든 정보를 초기화합니다.
     *
     * @description 약관 동의, 사용자 정보, 개인/법인 회원가입 정보를 모두 초기 상태로 되돌립니다.
     */
    const resetSignupStore = () => {
        // 회원가입 유형 초기화
        signUpType.value = undefined;
        corporateDocumentGuideAcknowledged.value = false;
        // 약관 동의 정보 초기화
        terms.TERMS_OF_USE = false;
        terms.PERSONAL_INFO_COLLECTION = false;
        terms.RISK_DISCLOSURE = false;
        terms.FAIS_DISCLOSURE = false;
        terms.POPIA_POLICY = false;
        terms.MARKETING_NOTIFICATION = false;
        // 사용자 기본 정보 초기화
        userInfo.ci = '';
        userInfo.name = '';
        userInfo.email = '';
        userInfo.phoneNo = '';
        userInfo.birth = '';
        userInfo.password = '';
        // 개인 회원가입 정보 초기화
        individualInfo.lastNameEn = '';
        individualInfo.firstNameEn = '';
        individualInfo.address = '';
        individualInfo.detailAddress = '';
        individualInfo.addressEn = '';
        individualInfo.detailAddressEn = '';
        individualInfo.zipCode = '';
        individualInfo.idCard = null;
        individualInfo.additionalIdDocument = null;
        // 법인 회원가입 정보 초기화
        corporateInfo.companyName = '';
        corporateInfo.businessNumber = '';
        corporateInfo.representativeLastName = '';
        corporateInfo.representativeFirstName = '';
        corporateInfo.representativeKorName = '';
        corporateInfo.representativeBirth = '';
        corporateInfo.address = '';
        corporateInfo.detailAddress = '';
        corporateInfo.addressEn = '';
        corporateInfo.detailAddressEn = '';
        corporateInfo.zipCode = '';
        corporateInfo.businessRegistration = null;
        corporateInfo.corporateRepresentative = null;
        corporateInfo.billPaymentCorporate = null;
        corporateInfo.shareholderRegister = null;
        corporateInfo.corporateRepresentativePassport = null;
        corporateInfo.additionalCorporateRepresentativePassport = [];
    };
    return {
        signUpType,
        corporateDocumentGuideAcknowledged,
        terms,
        userInfo,
        individualInfo,
        corporateInfo,
        getIndividualSignupInfo,
        getCorporateSignupInfo,
        updateTerms,
        updateUserInfo,
        updateUserEmail,
        updateUserPassword,
        resetUserInfo,
        resetUserEmail,
        resetUserPassword,
        resetTerms,
        updateIndividual,
        uploadIndividualDocument,
        resetIndividualDocument,
        updateCorporate,
        uploadCorpInfoDocument,
        uploadCorpAdminDocument,
        resetCorporateDocument,
        resetSignupStore,
    };
});
//# sourceMappingURL=useSignupStore.js.map